/**
 * Modernisierte Cash-X Login Seite
 * 
 * Design: Glassmorphism, Teal/Emerald Akzente, Smooth Animations
 * Features: Moderne Inputs, Subtle Background Effects
 */

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Eye, EyeOff, Loader2, ArrowLeft, Sparkles, Shield, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Get the page the user was trying to access
  const from = location.state?.from?.pathname || '/app';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setErrors({
        email: !formData.email ? 'E-Mail ist erforderlich' : '',
        password: !formData.password ? 'Passwort ist erforderlich' : ''
      });
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Anmeldung fehlgeschlagen');
      }
      
      // Store token
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('cashx_user', JSON.stringify(data.user));
      localStorage.setItem('cashx_tenant', JSON.stringify(data.tenant));
      
      // === NEUES BILLING WARNING SYSTEM ===
      if (data.billing_warning) {
        // Prüfe ob sich die Warning geändert hat
        const oldWarning = localStorage.getItem('cashx_billing_warning');
        const oldWarningParsed = oldWarning ? JSON.parse(oldWarning) : null;
        
        // Wenn Status sich geändert hat → Dismissed zurücksetzen
        if (!oldWarningParsed || oldWarningParsed.type !== data.billing_warning.type) {
          localStorage.removeItem('cashx_billing_warning_dismissed');
          localStorage.removeItem('cashx_billing_warning_dismissed_at');
        }
        
        localStorage.setItem('cashx_billing_warning', JSON.stringify(data.billing_warning));
        
        // Toast je nach Schweregrad
        if (data.billing_warning.type === 'soft_locked') {
          toast.error('Account eingeschränkt', {
            description: 'Neue Verkäufe sind gesperrt. Bitte Zahlung begleichen.',
            duration: 8000
          });
        } else if (data.billing_warning.type === 'grace') {
          toast.warning('Letzte Warnung', {
            description: `Sperre in ${data.billing_warning.days_until_lock} Tagen`,
            duration: 6000
          });
        } else if (data.billing_warning.type === 'overdue_warning') {
          toast.info('Zahlung offen', {
            description: data.billing_warning.message,
            duration: 5000
          });
        }
      } else {
        // Kein Billing Warning → alles aufräumen
        localStorage.removeItem('cashx_billing_warning');
        localStorage.removeItem('cashx_billing_warning_dismissed');
        localStorage.removeItem('cashx_billing_warning_dismissed_at');
      }
      
      // === LEGACY SOFT LOCK (für Kompatibilität) ===
      if (data.warning && data.warning.type === 'soft_lock' && !data.billing_warning) {
        localStorage.setItem('cashx_soft_lock_warning', JSON.stringify(data.warning));
      } else {
        localStorage.removeItem('cashx_soft_lock_warning');
        localStorage.removeItem('cashx_soft_lock_dismissed');
      }
      
      toast.success('Anmeldung erfolgreich!');
      
      // Super Admin → direkt zu /admin
      if (data.user.role === 'super_admin') {
        navigate('/admin');
      }
      // Check onboarding status
      else if (!data.tenant.onboarding_completed) {
        navigate('/smart-onboarding');
      } else {
        // Redirect to the page they were trying to access, or /app as default
        navigate(from, { replace: true });
      }
      
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Anmeldung fehlgeschlagen', {
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0B1220]">
      <Toaster position="top-center" />
      
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated gradient orbs */}
        <motion.div 
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(45,212,191,0.15) 0%, transparent 70%)',
          }}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.5, 0.7, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(34,197,94,0.12) 0%, transparent 70%)',
          }}
          animate={{
            scale: [1, 1.15, 1],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        <motion.div 
          className="absolute top-1/3 left-1/4 w-[300px] h-[300px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(47,128,255,0.08) 0%, transparent 70%)',
          }}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
      </div>
      
      <div className="relative w-full max-w-md px-4 py-12">
        {/* Back to Landing */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Link 
            to="/"
            data-testid="back-to-landing"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-teal-400 mb-8 transition-colors group"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Zurück zur Startseite
          </Link>
        </motion.div>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-8"
        >
          <Link to="/" className="inline-block mb-6">
            <motion.img 
              src="/cash-x-logo.png" 
              alt="Cash-X Kassensystem" 
              className="h-20 w-auto mx-auto object-contain"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            />
          </Link>
          <h1 
            className="text-3xl sm:text-4xl font-bold text-white tracking-tight"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            data-testid="login-title"
          >
            Willkommen zurück
          </h1>
          <p className="text-slate-400 mt-2">
            Melden Sie sich in Ihrem Konto an
          </p>
        </motion.div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="
            relative overflow-hidden
            bg-[#111C32]/80 backdrop-blur-xl
            border border-slate-700/50
            shadow-[0_20px_60px_rgba(0,0,0,0.4)]
            p-6 sm:p-8
          ">
            {/* Decorative top border */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 via-emerald-500 to-blue-500" />
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Input */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300 text-sm font-medium">
                  E-Mail-Adresse
                </Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    data-testid="login-email"
                    placeholder="ihre@email.de"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="
                      h-12 bg-[#0B1220]/80 border-slate-700/70 
                      text-white placeholder:text-slate-500
                      focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20
                      transition-all duration-200
                    "
                  />
                </div>
                {errors.email && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-red-400" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="password" className="text-slate-300 text-sm font-medium">
                    Passwort
                  </Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-teal-400 hover:text-teal-300 transition-colors"
                  >
                    Passwort vergessen?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="login-password"
                    placeholder="Ihr Passwort"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="
                      h-12 bg-[#0B1220]/80 border-slate-700/70 
                      text-white placeholder:text-slate-500 pr-12
                      focus:border-teal-500/50 focus:ring-2 focus:ring-teal-500/20
                      transition-all duration-200
                    "
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="
                      absolute right-3 top-1/2 -translate-y-1/2 
                      text-slate-400 hover:text-teal-400 
                      transition-colors p-1
                    "
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full bg-red-400" />
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="login-submit"
                className="
                  w-full h-12
                  bg-gradient-to-r from-teal-500 to-emerald-500
                  hover:from-teal-400 hover:to-emerald-400
                  text-white font-medium
                  shadow-lg shadow-teal-500/20
                  hover:shadow-teal-500/30
                  transition-all duration-300
                "
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Wird angemeldet...
                  </>
                ) : (
                  <>
                    <Zap className="h-5 w-5 mr-2" />
                    Anmelden
                  </>
                )}
              </Button>

              {/* Register Link */}
              <p className="text-center text-slate-400 text-sm pt-2">
                Noch kein Konto?{' '}
                <Link 
                  to="/register" 
                  className="text-teal-400 hover:text-teal-300 font-medium transition-colors"
                >
                  Jetzt registrieren
                </Link>
              </p>
            </form>
          </Card>
        </motion.div>

        {/* Trust badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 flex items-center justify-center gap-6"
        >
          <div className="flex items-center gap-2 text-slate-500 text-xs">
            <Shield className="w-4 h-4 text-emerald-500" />
            TSE-konform
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-xs">
            <Sparkles className="w-4 h-4 text-teal-500" />
            256-bit SSL
          </div>
        </motion.div>
      </div>
    </div>
  );
}
